import {
  Box,
  MenuItem,
  TextField,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, type ChangeEvent } from "react";
import { PaymentMethod } from "../../../generated/graphql";
import { type ApiOrderFormState } from "../api-order-form";
import { type SetApiOrderField } from "../hooks/use-api-order-form";
import ApiOrderSection from "./api-order-section";

const styles = {
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 2,
  } satisfies SxProps<Theme>,
};

const PAYMENT_METHODS = Object.values(PaymentMethod);

type ApiOrderDetailsSectionProps = {
  readonly houseAirwayBillNumber: string;
  readonly masterAirwayBillOfLadingNumber: string;
  readonly paymentMethod: ApiOrderFormState["paymentMethod"];
  readonly setField: SetApiOrderField;
};

/** The order's own identifiers and payment terms. */
const ApiOrderDetailsSection = ({
  houseAirwayBillNumber,
  masterAirwayBillOfLadingNumber,
  paymentMethod,
  setField,
}: ApiOrderDetailsSectionProps) => {
  const handleHouseAirwayBillNumberChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setField("houseAirwayBillNumber", event.target.value);
    },
    [setField],
  );

  const handleMasterAirwayBillOfLadingNumberChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setField("masterAirwayBillOfLadingNumber", event.target.value);
    },
    [setField],
  );

  const handlePaymentMethodChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setField(
        "paymentMethod",
        event.target.value as ApiOrderFormState["paymentMethod"],
      );
    },
    [setField],
  );

  return (
    <ApiOrderSection title="Order">
      <Box sx={styles.row}>
        <TextField
          required
          helperText="Reuse a HAWB to exercise duplicate-order merging."
          label="HAWB"
          size="small"
          value={houseAirwayBillNumber}
          onChange={handleHouseAirwayBillNumberChange}
        />
        <TextField
          label="MAWB"
          size="small"
          value={masterAirwayBillOfLadingNumber}
          onChange={handleMasterAirwayBillOfLadingNumberChange}
        />
        <TextField
          select
          label="Payment method"
          size="small"
          value={paymentMethod}
          onChange={handlePaymentMethodChange}
        >
          <MenuItem value="">Unset</MenuItem>
          {PAYMENT_METHODS.map((method) => (
            <MenuItem key={method} value={method}>
              {method}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </ApiOrderSection>
  );
};

export default ApiOrderDetailsSection;
